import {
    DataTableColumn,
    DataTableCustomized,
} from '@/components/ui/data-table-customized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import CustomerIcons from '@/icons/CustomerIcons'
import { Ban, CheckCircle2, Flame, Snowflake, Users } from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { getStatsByAssignee, StatsByAssigneePayload } from '../../../services'
import { AssigneeStats } from '../../../types/response'
import { TableSkeleton } from './skeletons'

const PAGE_SIZE = 10

interface StatsByAssigneeTableProps {
    filterPayload: StatsByAssigneePayload
}

const StatsByAssigneeTable: React.FC<StatsByAssigneeTableProps> = ({
    filterPayload,
}) => {
    const { t } = useTranslation()
    const [page, setPage] = useState(1)
    const [pageSize, setPageSize] = useState(PAGE_SIZE)

    const { data, isLoading, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.MARKETING,
                'stats-by-assignee',
                filterPayload,
            ],
            queryFn: () => getStatsByAssignee(filterPayload),
        },
        { loadingComponent: <TableSkeleton /> }
    )

    const stats = data?.results || []

    // Client-side pagination
    const paginatedStats = useMemo(() => {
        const start = (page - 1) * pageSize
        return stats.slice(start, start + pageSize)
    }, [stats, page, pageSize])

    const columns: DataTableColumn<AssigneeStats>[] = useMemo(
        () => [
            {
                key: 'employee_name',
                title: t('employee'),
                width: 180,
            },
            {
                key: 'total',
                title: t('total'),
                width: 70,
                headerClassName: 'text-center capitalize',
                cellClassName: 'text-center',
            },
            {
                key: 'unclassified',
                title: (
                    <span className="flex items-center justify-center gap-1 text-gray-500 capitalize">
                        <Users size={12} />
                        {t('unclassified')}
                    </span>
                ),
                width: 100,
                headerClassName: 'text-center',
                cellClassName: 'text-center text-gray-500',
            },
            {
                key: 'warm_lead',
                title: (
                    <span className="flex items-center justify-center gap-1 text-amber-600">
                        <Flame size={12} />
                        {t('warm-lead')}
                    </span>
                ),
                width: 90,
                headerClassName: 'text-center',
                cellClassName: 'text-center text-amber-600',
            },
            {
                key: 'sale_qualified_lead',
                title: (
                    <span className="flex items-center justify-center gap-1 text-green-600">
                        <CheckCircle2 size={12} />
                        SQL
                    </span>
                ),
                width: 70,
                headerClassName: 'text-center',
                cellClassName: 'text-center text-green-600',
            },
            {
                key: 'cool_lead',
                title: (
                    <span className="flex items-center justify-center gap-1 text-blue-600">
                        <Snowflake size={12} />
                        {t('cool-lead')}
                    </span>
                ),
                width: 90,
                headerClassName: 'text-center',
                cellClassName: 'text-center text-blue-600',
            },
            {
                key: 'un_qualified_lead',
                title: (
                    <span className="flex items-center justify-center gap-1 text-red-600">
                        <Ban size={12} />
                        {t('unqualified')}
                    </span>
                ),
                width: 100,
                headerClassName: 'text-center',
                cellClassName: 'text-center text-red-600',
            },
        ],
        [t]
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    return (
        <div className="bg-white rounded-md border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-2">
                <CustomerIcons className="text-primary w-4 h-4" />
                <Typogaphy className="font-medium text-primary capitalize">
                    {t('stats-by-assignee')}
                </Typogaphy>
            </div>

            <DataTableCustomized<AssigneeStats>
                columns={columns}
                rows={paginatedStats}
                loading={isLoading}
                emptyMessage={t('no-data')}
                rowKey={(row) => String(row.employee_id)}
                resizable
                pagination={{
                    page,
                    pageSize,
                    total: stats.length,
                    onPageChange: setPage,
                    onPageSizeChange: (size) => {
                        setPageSize(size)
                        setPage(1)
                    },
                    pageSizeOptions: [10, 20, 50],
                }}
            />
        </div>
    )
}

export default StatsByAssigneeTable
